import { debounce } from 'lodash';
import { getCurrentInstance, Plugin } from 'vue';
import { toast, ExternalToast } from 'vue-sonner';

const DEBOUNCE_TIME = 1000;
const DEBOUNCE_DEFAULT_OPTIONS = { leading: true, trailing: false };

export interface Alerts
{
    close(): void;
    success(message: string, options?: ExternalToast): void;
    debounceSuccess(message: string, options?: ExternalToast): void;
    info(message: string, options?: ExternalToast): void;
    debounceInfo(message: string, options?: ExternalToast): void;
    warning(message: string, options?: ExternalToast): void;
    debounceWarning(message: string, options?: ExternalToast): void;
    danger(message: string, options?: ExternalToast): void;
    debounceDanger(message: string, options?: ExternalToast): void;
    error(message: string, options?: ExternalToast): void;
    debounceError(message: string, options?: ExternalToast): void;
}

class MessagesHelper implements Alerts
{
    private toast: typeof toast;

    public constructor(_toast: typeof toast)
    {
        this.toast = _toast;
    }

    public close(): void
    {
        this.toast.dismiss();
    }

    // --------------------------------------------------

    public success(message: string, options: ExternalToast = {}): void
    {
        this.toast.success(message, options);
    }

    public debounceSuccess = debounce((message: string, options: ExternalToast = {}) =>
    {
        this.success(message, options);
    },
    DEBOUNCE_TIME, DEBOUNCE_DEFAULT_OPTIONS);

    // --------------------------------------------------

    public info(message: string, options: ExternalToast = {}): void
    {
        this.toast.info(message, options);
    }

    public debounceInfo = debounce((message: string, options: ExternalToast = {}) =>
    {
        this.info(message, options);
    },
    DEBOUNCE_TIME, DEBOUNCE_DEFAULT_OPTIONS);

    // --------------------------------------------------

    public warning(message: string, options: ExternalToast = {}): void
    {
        this.toast.warning(message, options);
    }

    public debounceWarning = debounce((message: string, options: ExternalToast = {}) =>
    {
        this.warning(message, options);
    },
    DEBOUNCE_TIME, DEBOUNCE_DEFAULT_OPTIONS);

    // --------------------------------------------------

    public danger(message: string, options: ExternalToast = {}): void
    {
        this.toast.error(message, options);
    }

    public debounceDanger = debounce((message: string, options: ExternalToast = {}) =>
    {
        this.danger(message, options);
    },
    DEBOUNCE_TIME, DEBOUNCE_DEFAULT_OPTIONS);

    // --------------------------------------------------

    public error(message: string, options: ExternalToast = {}): void
    {
        this.toast.error(message, options);
    }

    public debounceError = debounce((message: string, options: ExternalToast = {}) =>
    {
        this.error(message, options);
    },
    DEBOUNCE_TIME, DEBOUNCE_DEFAULT_OPTIONS);

    // --------------------------------------------------
}

export const useAlerts = (): {$alert: Alerts} =>
{
    const app = getCurrentInstance();

    return {
        $alert: app.appContext.config.globalProperties.$alert
    };
};

const AlertsPlugin: Plugin =
{
    install(app)
    {
        app.config.globalProperties.$alert = new MessagesHelper(toast);
    }
};

export default AlertsPlugin;

declare module "@vue/runtime-core"
{
    interface ComponentCustomProperties
    {
        $alert: Alerts;
    }
}
